import { useEffect, useRef, useState } from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { cn } from "./cn";
import { visibleConnectors } from "../lib/wallet-connectors";
import { useI18n } from "../i18n";

/**
 * WalletChip — compact wallet status widget for TopNavigation.
 *
 * WO#1 §21 rules: reuses the existing wagmi wallet architecture
 * (connect / disconnect / account). Does NOT reinvent connection,
 * network detection, network switching, or account state.
 *
 * States:
 *   - disconnected → "Connect" button opens a small connector menu
 *   - connected    → truncated address chip → link to #auth for
 *                    full session view (SIWE lives in the existing
 *                    AuthRoute — this chip does not duplicate it)
 */
export function WalletChip() {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { t } = useI18n();
  const menuConnectors = visibleConnectors(connectors);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click / escape (accessibility).
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isConnected && address) {
    const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
    return (
      <div ref={ref} className="relative shrink-0" data-testid="wallet-chip-connected">
        <button
          type="button"
          data-testid="wallet-chip-btn"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            "inline-flex items-center gap-2 h-7 px-3",
            "text-[10px] font-medium font-mono tracking-tight",
            "text-text-primary neon-glass pill-ghost",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
          )}
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 rounded-pill bg-state-success"
          />
          <span data-testid="wallet-chip-address">{short}</span>
        </button>
        {open && (
          <div
            role="menu"
            data-testid="wallet-chip-menu"
            className={cn(
              "absolute right-0 top-[calc(100%+6px)] z-modal",
              "min-w-[200px] rounded-md",
              "bg-panel border border-border-soft shadow-panel",
              "p-1",
            )}
          >
            <a
              href="#dashboard"
              role="menuitem"
              data-testid="wallet-chip-dashboard-link"
              onClick={() => setOpen(false)}
              className={cn(
                "block px-3 py-2 rounded-sm no-underline",
                "text-[12px] text-text-primary",
                "hover:bg-elevated",
              )}
            >
              {t("wallet.myTerritories")}
            </a>
            <a
              href="#auth"
              role="menuitem"
              data-testid="wallet-chip-session-link"
              onClick={() => setOpen(false)}
              className={cn(
                "block px-3 py-2 rounded-sm no-underline",
                "text-[12px] text-text-primary",
                "hover:bg-elevated",
              )}
            >
              {t("wallet.sessionDetails")}
            </a>
            <button
              type="button"
              role="menuitem"
              data-testid="wallet-chip-disconnect-btn"
              onClick={() => {
                disconnect();
                setOpen(false);
              }}
              className={cn(
                "block w-full text-left px-3 py-2 rounded-sm",
                "text-[12px] text-text-secondary",
                "hover:bg-elevated hover:text-text-primary",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
              )}
            >
              {t("wallet.disconnect")}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Disconnected state — connector menu
  return (
    <div ref={ref} className="relative shrink-0" data-testid="wallet-chip-disconnected">
      <button
        type="button"
        data-testid="wallet-chip-connect-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={isConnecting}
        className={cn(
          "inline-flex items-center gap-2 h-7 px-3",
          "font-mono text-[10px] font-medium tracking-[0.16em] uppercase",
          "text-text-primary no-underline",
          "neon-glass pill-ghost",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
          "disabled:opacity-60 disabled:cursor-not-allowed",
        )}
      >
        {isConnecting ? t("wallet.connecting") : t("wallet.connect")}
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path
            d="M3 6h6m0 0L6 3m3 3L6 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && menuConnectors.length > 0 && (
        <div
          role="menu"
          data-testid="wallet-chip-connectors"
          className={cn(
            "absolute right-0 top-[calc(100%+6px)] z-modal",
            "min-w-[220px] rounded-md",
            "bg-panel border border-border-soft shadow-panel",
            "p-1",
          )}
        >
          {menuConnectors.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              data-testid={`wallet-chip-connector-${c.id}`}
              onClick={() => {
                connect({ connector: c });
                setOpen(false);
              }}
              className={cn(
                "block w-full text-left px-3 py-2 rounded-sm",
                "text-[12px] text-text-primary",
                "hover:bg-elevated",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
              )}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
